import React, { forwardRef, useRef, useState } from "react";

import {
  ArrowUpward,
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from "@material-ui/icons";
import {
  Box,
  Button,
  Paper,
  TableCell,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import MaterialTable, { Icons, MTableBodyRow } from "material-table";

import { formatCurrency } from "@ds-developer/frontend";

import { Arrears } from "@hubcapp/types";
import { ArrearsTableProps, RowComponentProps } from "./types";

import ArrearsBreakdownModal from "./components/ArrearsBreakdownModal";

import useQueryInstructorArrearsBreakdown from "./hooks/useQueryInstructorArrearsBreakdown";
interface TableRef {
  dataManager: {
    pageSize: number;
  };
}

const tableIcons: Icons = {
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
};

const useStyles = makeStyles(theme => ({
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  title: {
    padding: theme.spacing(2),
  },
}));

const calculateSum = (values: number[]): number =>
  values.reduce((total, value) => total + value, 0);

const ArrearsTable: React.FC<ArrearsTableProps> = ({
  isLoading = false,
  arrears = [],
  id = "",
}) => {
  const tableRef = useRef<TableRef>(null);
  const [breakdownModalDisplay, onChangeBreakdownModalDisplay] = useState<
    boolean
  >(false);
  const [selectedArrears, setSelectedArrears] = useState<undefined | string>(
    undefined,
  );
  const toggleBreakdownModalDisplay = () =>
    onChangeBreakdownModalDisplay(!breakdownModalDisplay);

  const { data } = useQueryInstructorArrearsBreakdown({
    id,
    propositionKey: selectedArrears,
  });

  const handleViewClick = (arrearsItem: Arrears) => {
    setSelectedArrears(arrearsItem.id || "");
    toggleBreakdownModalDisplay();
  };

  return (
    <Box m={1}>
      <ArrearsBreakdownModal
        handleCancel={toggleBreakdownModalDisplay}
        modalDisplay={breakdownModalDisplay}
        data={data?.getArrearsDetailForProposition || []}
      />
      <MaterialTable
        icons={tableIcons}
        isLoading={isLoading}
        columns={[
          {
            title: "Reference",
            field: "reference",
          },
          {
            title: "Total",
            sorting: false,
            render: rowData =>
              formatCurrency(rowData.amount, false, {
                displayDecimalValue: true,
              }),
          },
        ]}
        actions={[
          arrearsItem => ({
            icon: () => (
              <Button
                component="label"
                size="small"
                color="secondary"
                variant="outlined"
                data-test="start-arrears-view-button"
                id="start-arrears-view-button"
              >
                View
              </Button>
            ),
            onClick: () => {
              handleViewClick(arrearsItem);
            },
            hidden: false,
          }),
        ]}
        options={{
          paging: false,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          draggable: false,
          actionsColumnIndex: -1,
          showTitle: false,
          actionsCellStyle: {
            justifyContent: "center",
          },
        }}
        localization={{
          header: { actions: "" },
          body: {
            emptyDataSourceMessage: "No arrears info to display",
          },
        }}
        components={{
          Toolbar: () => null,
          Container: props => <Paper {...props} elevation={0} />,
          Row: props => (
            <RowComponent
              arrears={arrears}
              isLastVisibleRow={
                tableRef.current?.dataManager?.pageSize === props.index + 1 ||
                arrears.length === props.index + 1
              }
              showSubtotalRow={true}
              {...props}
            />
          ),
        }}
        data={arrears}
      />
    </Box>
  );
};

export default ArrearsTable;

const RowComponent: React.FC<RowComponentProps> = ({
  arrears,
  isLastVisibleRow,
  showSubtotalRow,
  ...props
}) => {
  const classes = useStyles();
  const subtotalRowShouldRender =
    isLastVisibleRow && showSubtotalRow && arrears;

  return (
    <>
      <MTableBodyRow data-test="instructor-arrears-table-row" {...props} />
      {subtotalRowShouldRender && (
        <TableRow data-test="instructor-arrears-subtotal-row">
          <TableCell classes={{ root: classes.bold }}>Total debt</TableCell>
          <TableCell classes={{ root: classes.bold }}>
            <div data-label="total-debt">
              {formatCurrency(calculateSum(arrears.map(t => t.amount)))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
